/**
 * Phase 21 — optional add-ons offered by the applied package.
 *
 * These are deliberately NOT part of the base total (FR-008). The glass
 * shower door, for instance, is a $750–$1,100 range the contractor's own
 * sheets leave out of the computed figure — and the catalog holds a
 * single price per material, so showing it as a settled line would
 * misstate it. The customer opts in, and only then does it price.
 *
 * Adding one goes through the ordinary option picker (a selection in its
 * own option-group slot), so it behaves like any other choice afterwards
 * — including being swappable and removable.
 */

import type { PackageAddOn } from "@starz/shared/api/types.gen";

function formatUsd(cents: number): string {
  return (cents / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
}

interface Props {
  addOns: PackageAddOn[];
  /** Material ids already selected — those are no longer "add" offers. */
  selectedMaterialIds: string[];
  /** Record the add-on as a selection (same path as any option pick). */
  onAdd: (addOn: PackageAddOn) => void;
  disabled?: boolean;
}

export function PackageAddOns({
  addOns,
  selectedMaterialIds,
  onAdd,
  disabled = false,
}: Props) {
  // An add-on with no option group cannot be recorded as a selection at
  // all, so offering it would be a dead end.
  const offerable = addOns.filter(
    (a) => a.option_group_id && !selectedMaterialIds.includes(a.material_id),
  );
  if (offerable.length === 0) return null;

  return (
    <section
      aria-label="Optional add-ons"
      className="space-y-3 rounded-xl border border-starz-gold/20 bg-starz-cream/30 p-4"
    >
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-starz-gold">
          Optional add-ons
        </p>
        <p className="mt-1 text-xs text-warm-gray">
          Not included in the total above. Add one and we'll re-price.
        </p>
      </div>
      <ul className="space-y-2">
        {offerable.map((a) => (
          <li
            key={a.material_id}
            className="flex items-center justify-between gap-3 rounded-lg bg-white px-3 py-2"
          >
            <div className="min-w-0">
              <div className="truncate text-sm font-medium text-starz-navy">
                {a.display_name}
              </div>
              <div className="text-xs text-warm-gray">
                about {formatUsd(a.unit_price_cents)}
              </div>
            </div>
            <button
              type="button"
              onClick={() => onAdd(a)}
              disabled={disabled}
              className="min-h-[44px] shrink-0 rounded-lg border-2 border-starz-gold/30 px-4 py-2 text-sm font-semibold text-starz-navy transition-colors hover:border-starz-gold disabled:cursor-not-allowed disabled:opacity-50"
            >
              Add
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
